import { z } from 'zod'

/**
 * Preferencias de la app. Son pocas y se necesitan antes de pintar la primera pantalla (el tema,
 * por ejemplo), así que viven en localStorage y no en IndexedDB. Se incluyen en la copia de
 * seguridad. Cada valor se valida por separado: uno estropeado no se lleva por delante el resto.
 */
export const PEDAL_ACTIONS = [
  'next',
  'previous',
  'nextSection',
  'previousSection',
  'playPause',
] as const
export type PedalAction = (typeof PEDAL_ACTIONS)[number]

const KeyListSchema = z.array(z.string().min(1).max(40)).max(12)

export const PreferencesSchema = z.object({
  /** Notación con la que se muestran los acordes, sea cual sea la de la ficha. */
  notation: z.enum(['american', 'latin']),
  theme: z.enum(['dark', 'light', 'contrast']),
  /** Tamaño de la letra en la pantalla Tocar, en píxeles. */
  fontSize: z.number().int().min(14).max(64),
  /** Teclas (o pedal Bluetooth, que llega como teclado) de cada acción. */
  pedal: z.object({
    next: KeyListSchema,
    previous: KeyListSchema,
    nextSection: KeyListSchema,
    previousSection: KeyListSchema,
    playPause: KeyListSchema,
  }),
  /** Digitación elegida para cada acorde, por su nombre en notación americana. */
  voicings: z.record(z.string(), z.number().int().min(0).max(40)),
  keepAwake: z.boolean(),
  /** Metrónomo durante la guía sincronizada. La cuenta de entrada suena siempre. */
  metronome: z.boolean(),
  /** Compases de cuenta de entrada al empezar o reanudar (§5.3). */
  countInBars: z.number().int().min(0).max(2),
  /** Cuántos BPM sube «Me sale» en la velocidad progresiva. */
  speedStep: z.number().int().min(1).max(20),
})

export type Preferences = z.infer<typeof PreferencesSchema>

export const DEFAULT_PREFERENCES: Preferences = {
  notation: 'american',
  theme: 'dark',
  fontSize: 28,
  pedal: {
    next: ['ArrowRight', 'ArrowDown', 'PageDown', 'Space'],
    previous: ['ArrowLeft', 'ArrowUp', 'PageUp'],
    nextSection: [],
    previousSection: [],
    playPause: ['Enter'],
  },
  voicings: {},
  keepAwake: true,
  metronome: true,
  countInBars: 1,
  speedStep: 4,
}

const KEY = 'atril.preferences.v1'

/** Mezcla lo guardado con los valores por defecto, campo a campo. */
export function parsePreferences(raw: unknown): Preferences {
  const result: Preferences = structuredClone(DEFAULT_PREFERENCES)
  if (typeof raw !== 'object' || raw === null) return result
  const stored = raw as Record<string, unknown>

  const fields = PreferencesSchema.shape
  for (const name of Object.keys(fields) as (keyof Preferences)[]) {
    if (name === 'pedal') continue
    const parsed = fields[name].safeParse(stored[name])
    if (parsed.success) (result as Record<string, unknown>)[name] = parsed.data
  }
  const pedal = stored.pedal
  if (typeof pedal === 'object' && pedal !== null) {
    for (const action of PEDAL_ACTIONS) {
      const parsed = KeyListSchema.safeParse((pedal as Record<string, unknown>)[action])
      if (parsed.success) result.pedal[action] = parsed.data
    }
  }
  return result
}

/** Lo mínimo de `Storage` que necesitamos: así se puede probar sin navegador. */
export interface KeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/** ¿Hay ya algo guardado? Falso en el primer arranque de un dispositivo. */
export function hasStoredPreferences(storage: KeyValueStorage | null): boolean {
  try {
    return storage?.getItem(KEY) != null
  } catch {
    return false
  }
}

export function loadPreferences(storage: KeyValueStorage | null): Preferences {
  try {
    const raw = storage?.getItem(KEY)
    return parsePreferences(raw ? JSON.parse(raw) : null)
  } catch {
    return structuredClone(DEFAULT_PREFERENCES)
  }
}

export function savePreferences(storage: KeyValueStorage | null, preferences: Preferences): void {
  try {
    storage?.setItem(KEY, JSON.stringify(preferences))
  } catch {
    // Sin almacenamiento (modo privado): la app funciona, pero no recuerda los ajustes.
  }
}

/** Nombre de una tecla tal como se guarda: el espacio se escribe «Space». */
export function keyId(event: { key: string }): string {
  return event.key === ' ' ? 'Space' : event.key
}

/** La acción asociada a una tecla, si hay alguna. */
export function actionForKey(preferences: Preferences, key: string): PedalAction | null {
  for (const action of PEDAL_ACTIONS) {
    if (preferences.pedal[action].includes(key)) return action
  }
  return null
}
